import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ScaleIcon,
  CurrencyPoundIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';

gsap.registerPlugin(ScrollTrigger);

export default function InsightsCMAConsent() {
  usePageMeta(
    'Consent is where regulation is heading | SmileConsent Insights',
    'What the CMA is flagging in pricing and consumer choice is really a consent problem: understanding, options, comparability, and a documented decision process.',
    'https://smileconsent.co.uk/insights/consent-is-coming'
  );

  const heroRef = useRef(null);

  useEffect(() => {
    if (!heroRef.current) return;
    const tl = gsap.timeline();
    tl.from(heroRef.current.querySelectorAll('[data-hero]'), {
      y: 18,
      opacity: 1,
      duration: 0.7,
      ease: 'power3.out',
      stagger: 0.08,
    });

    gsap.utils.toArray('.animate-on-scroll').forEach((el) => {
      gsap.fromTo(
        el,
        { y: 26, opacity: 1 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 82%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });
  }, []);

  const sections = useMemo(
    () => [
      { id: 'read-between-lines', label: 'Read between the lines' },
      { id: 'where-regulation-is-heading', label: 'Where regulation is heading' },
      { id: 'real-risk', label: 'The real risk' },
      { id: 'blueprint', label: 'The blueprint' },
      { id: 'independents', label: 'Why independents can win' },
      { id: 'mindset-shift', label: 'Mindset shift' },
      { id: 'bottom-line', label: 'Bottom line' },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'Insights', href: '/insights/consent-is-coming' },
          ]}
        />
      </div>

      {/* Hero */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-gradient-to-br from-emerald-900/10 via-blue-600/10 to-indigo-700/10"
      >
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(37,99,235,0.28),_transparent_55%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/15 via-white/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-18 relative z-10">
          <div className="max-w-3xl">
            <div
              data-hero
              className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm"
            >
              <ScaleIcon className="h-4 w-4" />
              Insight: regulation, pricing, and consent
            </div>

            <h1
              data-hero
              className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 [text-shadow:0_1px_0_rgba(255,255,255,0.65)]"
            >
              The CMA may be talking about prices — but this is really about consent.
            </h1>

            <p data-hero className="mt-5 text-lg text-gray-700 leading-relaxed">
              Not the signed form at the end.
              <span className="font-semibold text-gray-900"> The process.</span>
            </p>

            <div data-hero className="mt-7 flex flex-col sm:flex-row gap-3">
              <a
                href="#read-between-lines"
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg shadow-blue-900/15 hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                Read the insight
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </a>
              <a
                href="/Summary_of_final_report_24.3.26.pdf"
                download
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white/95 px-6 py-3 font-semibold text-gray-900 hover:bg-white transition-colors shadow-sm"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-blue-700" />
                Download full article (PDF)
              </a>
              <Link
                to="/dental-consent-plans"
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white/95 px-6 py-3 font-semibold text-gray-900 hover:bg-white transition-colors shadow-sm"
              >
                View plans
              </Link>
            </div>

            <div data-hero className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <CurrencyPoundIcon className="h-5 w-5 text-blue-600" />
                  Transparency
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Clear costs before commitment.
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <UserGroupIcon className="h-5 w-5 text-emerald-600" />
                  Choice
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Options documented and comparable.
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-indigo-600" />
                  Proof
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Evidence of understanding, not just signatures.
                </p>
              </div>
            </div>
          </div>

          {/* TOC */}
          <div className="mt-10 lg:mt-0 lg:absolute lg:right-8 lg:top-16 lg:w-[380px]">
            <div className="rounded-2xl border border-gray-200 bg-white/98 backdrop-blur p-5 shadow-lg">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                On this page
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-700 transition-colors"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
              <div className="mt-4 rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 border border-blue-100 p-4">
                <div className="flex items-start gap-3">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-700 mt-0.5" />
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      Key question
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      Did the patient truly understand what they agreed to?
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <article className="lg:col-span-8">
            <section id="read-between-lines" className="animate-on-scroll scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Read between the lines
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                The Competition and Markets Authority may be talking about prices,
                competition and consumer experience — but if you read between the
                lines, this is really about consent.
              </p>
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-emerald-700 mt-0.5" />
                  <div>
                    <div className="text-sm font-bold text-emerald-900">
                      Consent isn’t a moment.
                    </div>
                    <div className="mt-1 text-sm text-emerald-900/90">
                      It’s a journey: understanding cost, options, and the freedom to decide.
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="where-regulation-is-heading" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Consent is where regulation is heading (whether it says so or not)
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                When regulators talk about price transparency, treatment options,
                consumer choice, complaints and redress — they’re describing the
                components of valid consent.
              </p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: CurrencyPoundIcon, title: 'Cost before commitment', text: 'Do they understand the cost before they commit?' },
                  { icon: DocumentTextIcon, title: 'Options and comparability', text: 'Do they understand all reasonable options — including doing nothing?' },
                  { icon: UserGroupIcon, title: 'Freedom to choose', text: 'Do they feel free to choose, without pressure or bias?' },
                  { icon: ChatBubbleLeftRightIcon, title: 'Reflect and question', text: 'Can they reflect, question, and decide?' },
                ].map((c) => (
                  <div key={c.title} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-blue-50 p-3 border border-blue-100">
                        <c.icon className="h-6 w-6 text-blue-700" />
                      </div>
                      <div className="font-bold text-gray-900">{c.title}</div>
                    </div>
                    <p className="mt-3 text-sm text-gray-700 leading-relaxed">{c.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-7 rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 mt-0.5" />
                  <div>
                    <div className="text-sm font-bold text-gray-900">
                      If any of those are shaky…
                    </div>
                    <div className="mt-1 text-sm text-gray-700">
                      the issue is not commercial — it’s clinical, ethical, and legal.
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section id="real-risk" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The real risk isn’t your fees — it’s gaps in understanding
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                The practices most exposed won’t simply be the most expensive, the busiest,
                or the most corporate. They’ll be the ones where patients later say:
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {['“I didn’t realise…”', '“No one told me…”', '“I felt rushed…”'].map((q) => (
                  <div key={q} className="rounded-2xl border border-rose-200 bg-rose-50 p-5">
                    <div className="text-lg font-extrabold text-rose-900">{q}</div>
                    <div className="mt-2 text-sm text-rose-900/80">
                      That’s a consent breakdown.
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="blueprint" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The veterinary report is a warning — and a blueprint
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                What the CMA effectively outlined is a standardised consent journey:
              </p>

              <div className="mt-6 space-y-3">
                {[
                  'Clear information before attendance',
                  'Written estimates before commitment',
                  'Structured options during decision-making',
                  'Transparent billing after treatment',
                  'A clear route to challenge or complain afterwards',
                ].map((s) => (
                  <div key={s} className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <CheckCircleIcon className="h-6 w-6 text-emerald-600 mt-0.5" />
                    <div className="text-gray-800 font-medium">{s}</div>
                  </div>
                ))}
              </div>

              <div className="mt-7 rounded-2xl border border-indigo-200 bg-indigo-50 p-6">
                <div className="text-sm font-bold text-indigo-900">
                  Consent shouldn’t live in the surgery —
                </div>
                <div className="mt-1 text-sm text-indigo-900/90">
                  it should run through the entire patient journey.
                </div>
              </div>
            </section>

            <section id="independents" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                This is where independent practices can win
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                The shift plays to your strengths — time for conversations, continuity of care, personal trust.
                But trust alone is no longer enough. Patients and regulators increasingly want
                <span className="font-semibold text-gray-900"> evidence of understanding</span>, not just intention.
              </p>
            </section>

            <section id="mindset-shift" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The mindset shift
              </h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Old model</div>
                  <div className="mt-2 text-lg font-bold text-gray-900">
                    Explain well, document enough, move on.
                  </div>
                </div>
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 shadow-sm">
                  <div className="text-xs font-bold text-blue-700 uppercase tracking-wide">New reality</div>
                  <div className="mt-2 text-lg font-bold text-blue-900">
                    Design a system where misunderstanding is difficult.
                  </div>
                </div>
              </div>
            </section>

            <section id="bottom-line" className="animate-on-scroll mt-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The bottom line
              </h2>
              <p className="mt-4 text-gray-700 leading-relaxed">
                The future of private dentistry will favour practices that can prove patients understood —
                not just that they were told.
              </p>
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-blue-50 p-6">
                <div className="text-sm font-bold text-gray-900">That proof?</div>
                <div className="mt-1 text-lg font-extrabold text-gray-900">
                  Consent — done properly.
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg shadow-blue-900/15 hover:from-blue-700 hover:to-indigo-700 transition-all"
                >
                  Start a free trial
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-6 py-3 font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
                >
                  Talk to us
                </Link>
              </div>
            </section>
          </article>

          {/* Sticky aside */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm animate-on-scroll">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-emerald-50 p-3 border border-emerald-100">
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">Practical takeaway</div>
                    <div className="text-sm text-gray-600">Design consent as infrastructure.</div>
                  </div>
                </div>
                <div className="mt-4 space-y-2 text-sm text-gray-700">
                  {[
                    'Make costs visible before commitment',
                    'Document options and the “do nothing” pathway',
                    'Keep interactions consistent (not clinician-dependent)',
                    'Record the decision as a process, not a moment',
                  ].map((t) => (
                    <div key={t} className="flex items-start gap-2">
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6 shadow-sm animate-on-scroll">
                <div className="text-sm font-bold text-blue-900">Explore</div>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <Link
                    to="/dental-consent-plans"
                    className="rounded-xl bg-white border border-blue-200 px-4 py-3 text-sm font-semibold text-blue-800 hover:bg-blue-50 transition-colors"
                  >
                    Consent plans
                  </Link>
                  <Link
                    to="/dental-consent-software"
                    className="rounded-xl bg-white border border-blue-200 px-4 py-3 text-sm font-semibold text-blue-800 hover:bg-blue-50 transition-colors"
                  >
                    Consent software
                  </Link>
                  <Link
                    to="/register"
                    className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:from-blue-700 hover:to-indigo-700 transition-all"
                  >
                    Start free trial
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Footer CTA */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white/10 border border-white/20 p-8 sm:p-10 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="text-2xl sm:text-3xl font-extrabold">
                  Build consent that’s clear, consistent, and provable.
                </div>
                <div className="mt-2 text-white/90">
                  Unlimited users on every plan. Simple monthly pricing. A consent journey your team can rely on.
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 font-bold text-blue-700 hover:bg-blue-50 transition-colors"
                >
                  Get started
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl border border-white/40 px-6 py-3 font-bold text-white hover:bg-white/10 transition-colors"
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

